import { Tabs } from '@design-systems/canvas-ui'
import { PageTabs } from 'components/page-tabs'
import { useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { MPMLayout } from 'components/layout'
import { ErrorFallback } from 'components/error-fallback'
import { BackButton } from 'components/button/back-button'
import { useLogClick } from 'utils/analytics'
import { useEssentials } from 'hooks/utils/useEssentials'
import { useRestricted } from 'hooks/utils/useRestricted'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import { MyTargetPerformanceProvider } from './myTarget-performance-context'
import { useMyTargetPerformanceMetricInfo } from 'hooks/myTarget-performance/useMyTargetPerformanceMetricInfo'

const TABS = ['guest', 'financial', 'team'] as const
type TabKey = (typeof TABS)[number]

export const MyTargetPerformance = () => {
    const { logClick } = useLogClick('myTarget-performance')
    const { t, env, session, currentLocation } = useEssentials()
    const { powerUserAccess } = useRestricted()
    const { myTargetPerformance } = TRANSLATION_KEYS
    const { tabs: tabKeys } = myTargetPerformance
    const title = t(myTargetPerformance.title)
    const navigate = useNavigate()
    const locationFromRouter = useLocation()

    // Filter tabs based on power user access
    const visibleTabs = useMemo(
        () => TABS.filter((tab) => tab !== 'team' || powerUserAccess),
        [powerUserAccess],
    )

    // Fetch shared data for all tabs
    const { data, isLoading, isError, refetch, error } =
        useMyTargetPerformanceMetricInfo({
            env,
            accessToken: session?.accessToken || '',
            location: currentLocation,
        })

    const currentPath =
        locationFromRouter.pathname.split('/').pop() || visibleTabs[0]
    const activeTab: TabKey = (visibleTabs as readonly string[]).includes(
        currentPath,
    )
        ? (currentPath as TabKey)
        : visibleTabs[0]

    const tabLabels = useMemo(
        () => ({
            guest: t(tabKeys.guest),
            financial: t(tabKeys.financial),
            team: t(tabKeys.team),
        }),
        [t, tabKeys],
    )

    const handleTabSelect = (activeTab: string) => {
        logClick('myTarget-performance tab ' + activeTab)
        const fullPath = `/mytarget-performance/${activeTab}${locationFromRouter.search}`
        if (locationFromRouter.pathname !== fullPath) {
            void navigate(fullPath)
        }
    }

    const contextValue = {
        data,
        isLoading,
        isError,
        refetch,
        error,
    }

    const isDetailsView = locationFromRouter.pathname.includes('/details/')

    const detailsMetricName = isDetailsView
        ? (locationFromRouter.state?.metric?.displayName as
              | string
              | undefined) ||
          decodeURIComponent(
              locationFromRouter.pathname.split('/details/')[1] || '',
          )
        : ''

    const heading = isDetailsView ? detailsMetricName : title

    return (
        <ErrorBoundary
            fallbackRender={({ error, resetErrorBoundary }) => (
                <ErrorFallback
                    error={error}
                    resetErrorBoundary={resetErrorBoundary}
                    heading={heading}
                />
            )}
        >
            <MyTargetPerformanceProvider value={contextValue}>
                <MPMLayout
                    heading={heading}
                    className="hc8-ph-none my-target-performance-page"
                    breadcrumbsEnabled={true}
                    isMenuOverride={isDetailsView}
                    menuOverride={<BackButton />}
                    hasTabs={!isDetailsView}
                    tabs={
                        !isDetailsView && (
                            <PageTabs
                                activeTab={activeTab}
                                className="tabs hc8-pv-2x"
                                disableAdaptive={true}
                                onTabSelect={handleTabSelect}
                            >
                                {visibleTabs.map((tab) => (
                                    <Tabs.Item key={tab} name={tab}>
                                        {tabLabels[tab]}
                                    </Tabs.Item>
                                ))}
                            </PageTabs>
                        )
                    }
                >
                    <div className="hc8-ph-4x">
                        <Outlet />
                    </div>
                </MPMLayout>
            </MyTargetPerformanceProvider>
        </ErrorBoundary>
    )
}
